export const TRACKING_PROTOCOL_VERSION = 1 as const
export const TRACKING_MESSAGE_KEY = 'GRILLD_TRACKING_BRIDGE' as const

export type CanonicalActionSource = 'web' | 'app_ios' | 'app_android'
export type CanonicalEntryMode = 'embedded' | 'standalone' | 'native'
export type TrackingConsentState = 'granted' | 'denied' | 'not_implemented'

export interface CanonicalTrackingItem {
  item_id: string
  item_name?: string
  price?: number
  quantity?: number
  item_category?: string
}

export interface CanonicalTrackingEvent {
  event_name: string
  event_id: string
  event_time: number
  environment: string
  action_source: CanonicalActionSource
  entry_mode: CanonicalEntryMode
  journey_id?: string
  page_url?: string
  top_level_page_url?: string
  referrer?: string
  value?: number
  currency?: string
  order_id?: string
  items?: CanonicalTrackingItem[]
  attribution?: TrackingAttribution
  user_data?: {
    email_sha256?: string
    phone_sha256?: string
    external_id_sha256?: string
    advertising_id?: string
  }
  consent: {
    analytics: TrackingConsentState
    advertising: TrackingConsentState
    att_status?: string
  }
  parameters?: Record<string, unknown>
}

export interface TrackingAttribution {
  source?: string
  medium?: string
  campaign?: string
  content?: string
  term?: string
  meta_click_id?: string
  pinterest_click_id?: string
  reddit_click_id?: string
  snapchat_click_id?: string
  tiktok_click_id?: string
  captured_at?: number
  last_activity_at?: number
}

interface TrackingMessageBase<T extends string> {
  key: typeof TRACKING_MESSAGE_KEY
  version: typeof TRACKING_PROTOCOL_VERSION
  type: T
}

export interface TrackingReadyMessage extends TrackingMessageBase<'ready'> {
  message_id: string
}

export interface TrackingInitMessage extends TrackingMessageBase<'init'> {
  message_id: string
  journey_id: string
  environment: string
}

export interface TrackingEventMessage extends TrackingMessageBase<'event'> {
  event: CanonicalTrackingEvent
}

export interface TrackingAckMessage extends TrackingMessageBase<'ack'> {
  event_id: string
}

export type TrackingBridgeMessage =
  | TrackingReadyMessage
  | TrackingInitMessage
  | TrackingEventMessage
  | TrackingAckMessage

export function toGtagEventParameters(event: CanonicalTrackingEvent): Record<string, unknown> {
  return {
    ...event.parameters,
    // These parameters populate GA's campaign/traffic-source fields. Keep the
    // attribution_* copies below for diagnostics and custom reporting.
    campaign_source: event.attribution?.source,
    campaign_medium: event.attribution?.medium,
    campaign_name: event.attribution?.campaign,
    campaign_content: event.attribution?.content,
    campaign_term: event.attribution?.term,
    event_id: event.event_id,
    event_time: event.event_time,
    environment: event.environment,
    action_source: event.action_source,
    entry_mode: event.entry_mode,
    journey_id: event.journey_id,
    page_url: event.page_url,
    top_level_page_url: event.top_level_page_url,
    referrer: event.referrer,
    attribution_source: event.attribution?.source,
    attribution_medium: event.attribution?.medium,
    attribution_campaign: event.attribution?.campaign,
    attribution_content: event.attribution?.content,
    attribution_term: event.attribution?.term,
    attribution_meta_click_id: event.attribution?.meta_click_id,
    attribution_pinterest_click_id: event.attribution?.pinterest_click_id,
    attribution_reddit_click_id: event.attribution?.reddit_click_id,
    attribution_snapchat_click_id: event.attribution?.snapchat_click_id,
    attribution_tiktok_click_id: event.attribution?.tiktok_click_id,
    attribution_captured_at: event.attribution?.captured_at,
    attribution_last_activity_at: event.attribution?.last_activity_at,
    user_data: event.user_data,
    consent_analytics: event.consent.analytics,
    consent_advertising: event.consent.advertising,
    consent_att_status: event.consent.att_status
  }
}

export function isCanonicalTrackingEvent(value: unknown): value is CanonicalTrackingEvent {
  if (!value || typeof value !== 'object') return false

  const event = value as Record<string, unknown>
  return (
    typeof event.event_name === 'string' &&
    typeof event.event_id === 'string' &&
    typeof event.event_time === 'number' &&
    typeof event.environment === 'string' &&
    ['web', 'app_ios', 'app_android'].includes(String(event.action_source)) &&
    ['embedded', 'standalone', 'native'].includes(String(event.entry_mode)) &&
    !!event.consent &&
    typeof event.consent === 'object'
  )
}

export function isTrackingBridgeMessage(value: unknown): value is TrackingBridgeMessage {
  if (!value || typeof value !== 'object') return false

  const message = value as Record<string, unknown>
  if (message.key !== TRACKING_MESSAGE_KEY || message.version !== TRACKING_PROTOCOL_VERSION) return false

  switch (message.type) {
    case 'ready':
      return typeof message.message_id === 'string'
    case 'init':
      return (
        typeof message.message_id === 'string' &&
        typeof message.journey_id === 'string' &&
        typeof message.environment === 'string'
      )
    case 'event':
      return isCanonicalTrackingEvent(message.event)
    case 'ack':
      return typeof message.event_id === 'string'
    default:
      return false
  }
}
